import React, { useState, useEffect } from 'react';
import {
  Card, CardContent, Typography, Box, Button, IconButton, TextField, Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import api from '../api';
import { useLanguage } from '../i18n/LanguageContext';

interface WeightLoggerProps {
  onWeightLogged: () => void;
  currentWeight?: number;
  targetWeight?: number;
}

const WeightLogger: React.FC<WeightLoggerProps> = ({ onWeightLogged, currentWeight, targetWeight }) => {
  const [weight, setWeight] = useState(currentWeight || 80);
  const today = new Date().toISOString().split('T')[0];
  const [date, setDate] = useState(today);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    if (currentWeight) setWeight(currentWeight);
  }, [currentWeight]);

  const adjustWeight = (delta: number) => {
    setWeight(prev => Math.round((prev + delta) * 10) / 10);
  };

  const handleSubmit = async () => {
    setSaving(true);
    setSuccess(false);
    setError(false);
    try {
      await api.post('/weight', { date, weight });
      setSuccess(true);
      onWeightLogged();
      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      console.error('Failed to log weight:', err);
      setError(true);
    } finally {
      setSaving(false);
    }
  };

  const remaining = targetWeight ? Math.round((weight - targetWeight) * 10) / 10 : null;

  return (
    <Card sx={{ borderRadius: 4 }}>
      <CardContent sx={{ textAlign: 'center' }}>
        {error && (
          <Alert severity="error" onClose={() => setError(false)} sx={{ mb: 1, textAlign: 'left' }}>
            {t.weightLogFailed}
          </Alert>
        )}
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {t.logWeight}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, my: 2 }}>
          <IconButton
            onClick={() => adjustWeight(-0.1)}
            color="primary"
            sx={{ border: 1, borderColor: 'divider' }}
          >
            <RemoveIcon />
          </IconButton>
          <Box>
            <Typography variant="h3" fontWeight="bold">
              {weight.toFixed(1)}
            </Typography>
            <Typography variant="caption" color="text.secondary">kg</Typography>
          </Box>
          <IconButton
            onClick={() => adjustWeight(0.1)}
            color="primary"
            sx={{ border: 1, borderColor: 'divider' }}
          >
            <AddIcon />
          </IconButton>
        </Box>

        {remaining !== null && remaining > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {remaining} {t.kgToTarget}
          </Typography>
        )}

        <TextField
          type="date"
          size="small"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          sx={{ mb: 2, width: '100%' }}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: today }}
          label={t.date}
        />

        <Button
          variant="contained"
          fullWidth
          onClick={handleSubmit}
          disabled={saving}
          color={success ? 'success' : 'primary'}
        >
          {saving ? t.saving : success ? t.saved : t.logWeight}
        </Button>
      </CardContent>
    </Card>
  );
};

export default WeightLogger;
